/**
 * API Routes Configuration
 *
 * This file contains all API endpoint configurations for the application.
 * It provides a centralized place to manage all API routes and makes
 * it easier to maintain and update endpoints.
 */

/**
 * Base API Configuration
 */
// 'https://jsonplaceholder.typicode.com'
export const API_CONFIG = {
    // eslint-disable-next-line no-undef
    BASE_URL: process.env.REACT_APP_API_ENDPOINT,
    VERSION: 'v4',
    TIMEOUT: 10000,
};

/**
 * Authentication Routes
 */
export const AUTH_ROUTES = {
    LOGIN: '/auth/login',
    LOGOUT: '/auth/logout',
    REGISTER: '/auth/register',
    FORGOT_PASSWORD: '/auth/forgot-password',
    RESET_PASSWORD: '/auth/reset-password',
    REFRESH_TOKEN: '/auth/refresh-token',
    VERIFY_EMAIL: '/auth/verify-email',
    CHANGE_PASSWORD: '/auth/change-password',
};

/**
 * User Management Routes
 */
export const USER_ROUTES = {
    GET_USERS: '/users',
    GET_USER: (id) => `/users/${id}`,
    CREATE_USER: '/users',
    UPDATE_USER: (id) => `/users/${id}`,
    DELETE_USER: (id) => `/users/${id}`,
    GET_USER_PROFILE: '/users/profile',
    UPDATE_USER_PROFILE: '/users/profile',
    UPLOAD_AVATAR: '/users/avatar',
};

/**
 * Customer Management Routes
 */
export const CUSTOMER_ROUTES = {
    GET_CUSTOMERS: '/customers',
    GET_CUSTOMER: (id) => `/customers/${id}`,
    CREATE_CUSTOMER: '/customers',
    UPDATE_CUSTOMER: (id) => `/customers/${id}`,

    SEARCH_CUSTOMERS: '/customers/search',
    GET_CUSTOMER_HISTORY: (id) => `/customers/${id}/history`,
    // Customer creation and update routes
    CHECK_PHONE_NUMBER: '/user/checkPhoneNumber',
    CREATE_CUSTOMER_MECHANIC: '/mechanic/createCustomer',
    UPDATE_CUSTOMER_DETAILS: (id) => `/user/updateCustomerDetails/${id}`,
    GET_SHOP_CREATED_CUSTOMER_DETAIL: (id) => `/user/customerDetail/${id}`,
    GET_SHOP_IMPORTED_CUSTOMER_DETAIL: (id) => `/user/getImportCustomer/${id}`,
    UPDATE_IMPORTED_CUSTOMER: '/user/create-customer-from-imported',
    DELETE_CUSTOMER: (id) => `/user/deleteUser/${id}`,
};

/**
 * Invoice Management Routes
 */
export const INVOICE_ROUTES = {
    GENERATE_INVOICE_NUMBER: '/invoice/get-next-invoice-number',
    INVOICE_LIST: '/invoice/list',
    GET_INVOICE: (id) => `/invoices/${id}`,
    CREATE_INVOICE: '/invoices',
    UPDATE_INVOICE: (id) => `/invoices/${id}`,
    DELETE_INVOICE: (id) => `/invoices/${id}`,
    SEND_INVOICE: (id) => `/invoices/${id}/send`,
    GET_INVOICE_PDF: (id) => `/invoices/${id}/pdf`,
    MARK_PAID: (id) => `/invoices/${id}/mark-paid`,
    GET_INVOICE_STATS: '/invoices/stats',
    /** GET ?vehicleId → { exists, draft } */
    FIND_OPEN_DRAFT: '/invoice/open-draft',
};

/**
 * Appointment Management Routes
 */
export const APPOINTMENT_ROUTES = {
    GET_APPOINTMENTS: '/appointments',
    GET_APPOINTMENT: (id) => `/appointments/${id}`,
    CREATE_APPOINTMENT: '/appointments',
    UPDATE_APPOINTMENT: (id) => `/appointments/${id}`,
    DELETE_APPOINTMENT: (id) => `/appointments/${id}`,
    GET_CALENDAR_EVENTS: '/appointments/calendar',
    RESCHEDULE_APPOINTMENT: (id) => `/appointments/${id}/reschedule`,
    CANCEL_APPOINTMENT: (id) => `/appointments/${id}/cancel`,
    // Additional appointment routes for existing functionality
    GET_APPOINTMENT_FOR_INVOICE: '/appointment/get-appointment-for-invoice',
    KANBAN_BOARD_LIST: '/appointment/appointmentListKanbanBoard',
    APPOINTMENT_DETAILS: '/appointment/detail',
    UPDATE_APPOINTMENT_STATUS: '/appointment/updateAppointmentStatus',
    APPOINTMENT_LIST: '/appointment/appointmentList',
    CREATE_APPOINTMENT_SAAS: '/appointment/createAppointmentFromSaas',
    RESCHEDULE_APPOINTMENT_SAAS: '/appointment/rescheduleAppointment',
    CANCEL_APPOINTMENT_SAAS: '/appointment/cancelAppointment',
    MECHANIC_DETAILS: '/appointment/getAssignList',
    PROPOSE_DATE_TIME: '/appointment/propose-new-time-saas',
    ADD_SERVICE: '/appointment/add-service',
    DELETE_SERVICE: '/appointment/delete-service',
    UPDATE_NOTE: '/appointment/update-appointment-note-for-customer',
    RESCHEDULE_ACCEPT_DECLINE: '/appointment/reschedule-accept-decline',
    SHOP_CLOSING_TIME: 'shop/shopClosingTime',
    APPOINTMENT_REQUEST_LIST: '/appointment/appointment-requests',
    APPOINTMENT_REQUEST_CARD_COUNT: 'appointment/appointment-requests/statistics',
};

/**
 * Quick Google Review Routes
 */
export const QUICK_GOOGLE_REVIEW_ROUTES = {
    GET_CARDS_COUNT: '/google-review-request/dashboard',
    GET_REQUESTS: '/google-review-request/list',
    GET_QUICK_REVIEW_USERS: '/user/quick-google-review-request/list',
    CREATE_REQUEST: '/google-review-request/create',
};

/**
 * Inventory Management Routes
 */
export const INVENTORY_ROUTES = {
    GET_INVENTORY: '/inventory',
    GET_INVENTORY_OPTIMIZED: '/inventory/list-of-parts', // Main route for optimized inventory list
    GET_INVENTORY_ITEM: (id) => `/inventory/${id}`,
    CREATE_INVENTORY_ITEM: '/inventory',
    UPDATE_INVENTORY_ITEM: (id, batchNumber) => `/inventory/update/${id}/${batchNumber}`,
    DELETE_INVENTORY_ITEM: (id, batchNumber) => `/inventory/delete/${id}/${batchNumber}`,
    SEARCH_INVENTORY: '/inventory/search',
    UPDATE_STOCK: (id) => `/inventory/${id}/stock`,
    GET_LOW_STOCK: '/inventory/low-stock',
    GET_BATCH_DETAILS: (batchNumber) => `/inventory-items/get-batch-details/${batchNumber}`,
    GET_INVENTORY_ITEMS: '/inventory-items/list',
    // Filter routes
    GET_FILTER_OPTIONS: '/inventory/filter',
    // Parts related routes
    PARTS: {
        LIST: '/parts/list',
        CREATE: '/parts/add',
        UPDATE: (id) => `/parts/update/${id}`,
        DELETE: (id) => `/parts/delete/${id}`,
    },
    // Brands and categories
    PARTS_BRAND: {
        LIST: '/parts-brand/list',
        CREATE: '/parts-brand/create',
    },
    PARTS_CATEGORY: {
        LIST: '/parts-category/list',
        CREATE: '/parts-category/create',
    },
    TYRE_MODEL: {
        LIST: '/tyre-model/list',
        CREATE: '/tyre-model/create',
    },
};

/**
 * Supplier Management Routes
 */
export const SUPPLIER_ROUTES = {
    GET_SUPPLIERS: '/suppliers',
    GET_SUPPLIER: (id) => `/suppliers/${id}`,
    CREATE_SUPPLIER: '/suppliers',
    UPDATE_SUPPLIER: (id) => `/suppliers/${id}`,
    DELETE_SUPPLIER: (id) => `/suppliers/${id}`,
    GET_SUPPLIER_PRODUCTS: (id) => `/suppliers/${id}/products`,
};

/**
 * Estimate Management Routes
 */
export const ESTIMATE_ROUTES = {
    GENERATE_ESTIMATE_NUMBER: '/estimate/get-next-estimate-number',
    ESTIMATE_LIST: '/estimate/estimateListSaas',
    GET_ESTIMATE: (id) => `/estimates/${id}`,
    CREATE_ESTIMATE: '/estimates',
    UPDATE_ESTIMATE: (id) => `/estimates/${id}`,
    DELETE_ESTIMATE: (id) => `/estimates/${id}`,
    CONVERT_TO_INVOICE: (id) => `/estimates/${id}/convert-to-invoice`,
    SEND_ESTIMATE: (id) => `/estimates/${id}/send`,
    /** GET ?vehicleId → { exists, draft } */
    FIND_OPEN_DRAFT: '/estimate/open-draft',
};

/**
 * Work Order Routes
 *
 * NOTE: endpoints are supplied by the API team page by page (see the spec, §10 Phase 0).
 * Paths below are placeholders and are NOT called yet — the module runs on mock data until
 * each screen's endpoint lands.
 */
export const WORK_ORDER_ROUTES = {
    // Confirmed endpoints
    GENERATE_WORK_ORDER_NUMBER: 'work-order-authorization/get-next-work-order-number',
    WORK_ORDER_LIST: 'work-order-authorization/list',
    CREATE_WORK_ORDER: 'work-order-authorization/create',
    UPDATE_WORK_ORDER: (id) => `work-order-authorization/update/${id}`,

    /** PUT { status } — one endpoint drives every status change (authorise, cancel, …). */
    CHANGE_STATUS: (id) => `work-order-authorization/change-status/${id}`,

    /**
     * POST — record an action against the work order so it appears in the history.
     * `type` is dynamic (download today, share later), so new actions need no new route.
     */
    REGISTER_ACTION: (type, id) => `work-order-authorization/register-action/${type}/${id}`,

    // Awaiting the API team — still served by mock fixtures
    GET_WORK_ORDER: 'work-order-authorization/get',
    DELETE_WORK_ORDER: (id) => `work-order-authorization/delete/${id}`,
    HISTORY: (id) => `work-order-authorization/history/${id}`,
    CONVERT_TO_INVOICE: (id) => `work-order-authorization/convert-to-invoice/${id}`,
    SEND_WORK_ORDER_EMAIL: (id) => `work-order-authorization/send-work-order-pdf/${id}`,
    /** GET ?vehicleId → { exists, draft } */
    FIND_OPEN_DRAFT: 'work-order-authorization/open-draft',
};

/**
 * Dashboard and Analytics Routes
 */
export const DASHBOARD_ROUTES = {
    GET_DASHBOARD_STATS: '/dashboard/stats',
    GET_REVENUE_CHART: '/dashboard/revenue-chart',
    GET_RECENT_ACTIVITIES: '/dashboard/recent-activities',
    GET_UPCOMING_APPOINTMENTS: '/dashboard/upcoming-appointments',
    GET_PENDING_INVOICES: '/dashboard/pending-invoices',
};

/**
 * Notification Routes
 */
export const NOTIFICATION_ROUTES = {
    GET_NOTIFICATIONS: '/notifications',
    MARK_READ: (id) => `/notifications/${id}/read`,
    MARK_ALL_READ: '/notifications/mark-all-read',
    DELETE_NOTIFICATION: (id) => `/notifications/${id}`,
    GET_UNREAD_COUNT: '/notifications/unread-count',
};

/**
 * Settings Routes
 */
export const SETTINGS_ROUTES = {
    GET_SETTINGS: '/settings',
    UPDATE_SETTINGS: '/settings',
    GET_SHOP_PROFILE_DETAIL: '/user/shopOwnerDetail',
    UPDATE_SHOP_PROFILE_DETAIL: `user/updatePersonalInfo`,
    GET_BUSINESS_INFO: '/settings/business',
    UPDATE_BUSINESS_INFO: '/settings/business',
    GET_EMAIL_TEMPLATES: '/settings/email-templates',
    UPDATE_EMAIL_TEMPLATE: (id) => `/settings/email-templates/${id}`,
    GET_DISCOUNT_PROGRAMS: '/membership-program/list',

    UPDATE_DROP_OFF_SLOTS_CONFIG: 'shop/update-dropoff-slots-config',
    GET_SLOTS_WITH_DISABLED: 'shop/get-slots-with-disabled',
    UPDATE_DISABLED_SLOTS: 'shop/disabled-slots',
    GET_DISABLED_SLOTS: 'shop/get-disabled-slots',
    DELETE_DISABLED_SLOT: (id) => `shop/disabled-slots/${id}`,
    GET_AVAILABLE_DATES: 'shop/get-dates',
    GET_APPOINTMENTS_BY_SLOT: 'shop/appointments/by-slot',
    UPDATE_BUFFER_CONFIG: 'shop/update-buffer-config',
    GET_SHOP_TAXES: 'shopTaxes/list',
    UPDATE_SHOP_TAXES: (id) => `shopTaxes/update/${id}`,
    SEND_TEST_THANK_YOU_EMAIL: 'shop/test-thankyou-email-settings',
    UPDATE_THANK_YOU_EMAIL: 'shop/update-thankyou-email-settings',
    GET_THANK_YOU_EMAIL: 'shop/thankyou-email-settings',
    UPDATE_SHOP_CONFIG: 'shop/update-shop-config',
    // Shop Configuration Routes
    GET_SHOP_CONFIG: 'shop/config?',
    GET_TERMS_AND_WARRANTY: 'shop/termsAndWarranty',
    GET_SEQUENCE_NUMBER: 'shop-module-sequence-config/get-sequence-number',
    UPDATE_SEQUENCE_NUMBER: 'shop-module-sequence-config/update-sequence',

    //PDF SETTINGS VISIBILITY
    GET_VISIBILITY_SETTING: 'pdf-setting/visibility-list',
    UPDATE_VISIBILITY_SETTING: 'pdf-setting/visibility-update',

    // Customer Notification Templates
    CUSTOMER_NOTIFICATION_TEMPLATES: 'customer-notification-templates',
};

/**
 * Service Routes
 */
export const SERVICE_ROUTES = {
    GET_SHOP_SERVICES: 'shopServices/shopServices',
    CREATE_SERVICE_SUBCATEGORY: 'serviceSubCategory/create-with-shop-services',
};

/**
 * Saved Service Routes
 */
export const SAVEDSERVICE_ROUTES = {
    GET_SAVED_SERVICES: 'saved-services/list',
    GET_SAVED_SERVICE: (id) => `saved-services/get/${id}`,
    CREATE_SAVED_SERVICE: 'saved-services/create',
    UPDATE_SAVED_SERVICE: (id) => `saved-services/update/${id}`,
    DELETE_SAVED_SERVICE: (id) => `saved-services/delete/${id}`,
    CHECK_SAVED_SERVICE: 'saved-services/check-exist',
    GET_GROUPED_SAVED_SERVICES: 'saved-services/grouped-list',
    UPDATE_BY_CATEGORY: (id) => `saved-services/update-by-service-sub-category/${id}`,
};

/**
 * Master Data Routes
 */
export const MASTER_ROUTES = {
    GET_COUNTRY_MASTER_LIST: 'countryMaster/list-of-country',
    GET_PROVINCE_MASTER_LIST: 'provinceMaster/list-of-province',
    GET_CITY_MASTER_LIST: 'cityMaster/list-of-city',
    GET_VEHICLE_YEAR_LIST: 'vehicle/vehicleYear',
    GET_VEHICLE_MAKE_LIST: 'vehicleMake/list',
    GET_VEHICLE_MODEL_LIST: 'vehicleModel/experimental/list',
    GET_VEHICLE_TRIM_LIST: 'vehicleModelTrim/list',
    GET_VEHICLE_ENGINE_LIST: 'vehicleEngine/list',
    GET_VEHICLE_TRANSMISSION_LIST: 'vehicleTransmission/list',
    GET_VEHICLE_FUEL_TYPE_LIST: 'vehicleFuelType/list',
    GET_VEHICLE_DRIVE_TYPE_LIST: 'vehicleDriveType/list',
    GET_VEHICLE_SEAT_COUNT_LIST: 'vehicleSeatCount/list',
    GET_SERVICE_CATEGORY_LIST: 'serviceCategory/list',
    GET_SELECTED_SERVICE_CATEGORY_LIST: 'saved-services/category-list',
    GET_MECHANIC_DETAILS: 'appointment/getAssignList',
    // Mutation routes
    CREATE_VEHICLE_MAKE: 'vehicleMake/create',
    CREATE_VEHICLE_MODEL: 'vehicleModel/experimental/create',
    CREATE_VEHICLE_TRIM: 'vehicleModelTrim/new-trim/create',
    DECODE_VIN: 'user/decode/',
    DECODE_LICENSE: 'user/decode-liscence-number-plate',
};

/**
 * Vehicle Management Routes
 */
export const VEHICLE_ROUTES = {
    GET_VEHICLE_LIST: '/vehicles',
    GET_VEHICLE_DETAILS: '/vehicles/:vehicleId',
    CREATE_VEHICLE: '/vehicle/addVehicle',
    UPDATE_VEHICLE: '/vehicle/updateVehicle/',
    DELETE_VEHICLE: (vehicleId) => `/vehicle/deleteVehicle/${vehicleId}`,
    ADD_IMPORTED_VEHICLE: (importedVehicleId) => `/vehicle/imported-vehicle-create/${importedVehicleId}`,
    UPDATE_IMPORTED_VEHICLE: (vehicleId) => `/vehicle/imported-vehicle-update/${vehicleId}`,
    DELETE_IMPORTED_VEHICLE: (vehicleId) => `/vehicle/imported-vehicle-delete/${vehicleId}`,
    GET_VEHICLE_LIST_BY_USER: (userId) => `vehicle/list/${userId}`,
};

/**
 * Shop Settings Routes
 */
export const SHOP_ROUTES = {
    GET_DEFAULT_SHOP_PRICE: 'shop/default-shop-price',
    GET_SHOP_DETAILS: 'shop/shopDetails',
    UPDATE_SHOP_DETAILS: 'shop/shopBasicsUpdate',
};

/**
 * File Upload Routes
 */
export const FILE_ROUTES = {
    UPLOAD_FILE: '/files/upload',
    DELETE_FILE: (id) => `/files/${id}`,
    GET_FILE: (id) => `/files/${id}`,
    GET_FILE_URL: (id) => `/files/${id}/url`,
};

/**
 * Example/Demo Routes (using JSONPlaceholder for demonstration)
 */
export const DEMO_ROUTES = {
    GET_POSTS: '/posts',
    GET_POST: (id) => `/posts/${id}`,
    CREATE_POST: '/posts',
    UPDATE_POST: (id) => `/posts/${id}`,
    DELETE_POST: (id) => `/posts/${id}`,
    GET_COMMENTS: '/comments',
    GET_POST_COMMENTS: (postId) => `/posts/${postId}/comments`,
};

/**
 * Route Builder Helper
 *
 * Helper function to build complete URLs with base URL
 */
export const buildApiUrl = (route, params = {}) => {
    let url = route;

    // Replace path parameters
    Object.keys(params).forEach((key) => {
        url = url.replace(`:${key}`, params[key]);
    });

    return url;
};

/**
 * Query Parameter Helper
 *
 * Helper function to build query strings
 */
export const buildQueryString = (params) => {
    const searchParams = new URLSearchParams();

    Object.keys(params).forEach((key) => {
        if (params[key] !== undefined && params[key] !== null && params[key] !== '') {
            searchParams.append(key, params[key]);
        }
    });

    return searchParams.toString();
};

/**
 * Complete URL Builder
 *
 * Helper function to build complete URLs with query parameters
 */
export const buildCompleteUrl = (route, pathParams = {}, queryParams = {}) => {
    const url = buildApiUrl(route, pathParams);
    const queryString = buildQueryString(queryParams);

    return queryString ? `${url}?${queryString}` : url;
};

/**
 * Export all routes as a single object for convenience
 */
export const API_ROUTES = {
    AUTH: AUTH_ROUTES,
    USER: USER_ROUTES,
    CUSTOMER: CUSTOMER_ROUTES,
    INVOICE: INVOICE_ROUTES,
    APPOINTMENT: APPOINTMENT_ROUTES,
    INVENTORY: INVENTORY_ROUTES,
    SUPPLIER: SUPPLIER_ROUTES,
    ESTIMATE: ESTIMATE_ROUTES,
    WORK_ORDER: WORK_ORDER_ROUTES,
    DASHBOARD: DASHBOARD_ROUTES,
    NOTIFICATION: NOTIFICATION_ROUTES,
    SETTINGS: SETTINGS_ROUTES,
    SERVICE: SERVICE_ROUTES,
    SAVEDSERVICE: SAVEDSERVICE_ROUTES,
    MASTER: MASTER_ROUTES,
    VEHICLE: VEHICLE_ROUTES,
    SHOP: SHOP_ROUTES,
    FILE: FILE_ROUTES,
    DEMO: DEMO_ROUTES,
};

export default API_ROUTES;
